import type { CreditNoteResponse } from '@palaccount/contracts';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount } from '@/components/amount';
import { Gated } from '@/components/permission-gate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { localizeServerCause } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { computeApplyView } from './apply-credit';
import { useApplyCreditNote, useCustomerUnappliedCreditNotes } from './returns-api';

/** One open invoice the receipts grid has on screen. */
export interface SourceInvoice {
  invoiceId: string;
  invoiceCode: string;
  balanceDue: string;
}

/**
 * Wave 4A — the receipts screen's SECOND source of settlement: the
 * customer's credit notes with unapplied credit, each offered as "Apply
 * credit note CN-#### — 30.00 available" with an amount cell per open
 * invoice on the page. Applying sends the positive cells to the note's
 * `apply` route — no voucher is involved, no money moves (the note's own
 * entry credited 1121 when it posted), and the open-invoice grid refreshes
 * with the reduced balances.
 *
 * Absent when the customer has no such credit: a section that only ever
 * says "none" would be noise under every cash receipt.
 */
export function CreditNoteSources({
  partnerId,
  currency,
  invoices,
}: {
  partnerId: string;
  currency: string;
  invoices: readonly SourceInvoice[];
}) {
  const { t } = useTranslation();
  const { data: notes } = useCustomerUnappliedCreditNotes(partnerId || null);
  if (!notes || notes.length === 0) return null;
  return (
    <section className="mt-4 grid gap-3" aria-label={t('receipts.creditSourceTitle')}>
      <h3 className="text-sm font-semibold">{t('receipts.creditSourceTitle')}</h3>
      {notes.map((note) => (
        <CreditNoteSource key={note.id} note={note} currency={currency} invoices={invoices} />
      ))}
    </section>
  );
}

function CreditNoteSource({
  note,
  currency,
  invoices,
}: {
  note: CreditNoteResponse;
  currency: string;
  invoices: readonly SourceInvoice[];
}) {
  const { t } = useTranslation();
  const applyMutation = useApplyCreditNote(note.id);
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const view = useMemo(
    () => computeApplyView(amounts, invoices, note.unappliedTotal, currency),
    [amounts, invoices, note.unappliedTotal, currency],
  );

  const onApply = async () => {
    if (view.allocations.length === 0 || view.over) return;
    setError(null);
    try {
      await applyMutation.mutateAsync({ allocations: view.allocations });
      setAmounts({});
    } catch (thrown) {
      setError(localizeServerCause(t, thrown, 'returns.errSaveFailed'));
    }
  };

  // A linked note with no application yet already nets its own invoice
  // (invoice-settlement.ts, term (b)); applying it elsewhere moves it.
  const standing = note.invoiceCode !== null && note.applications.length === 0;

  return (
    <div className="grid gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
      <div className="flex flex-wrap items-center gap-2">
        <Link to={`/sales/returns/${note.id}`} className="font-medium hover:underline">
          {t('receipts.applyCreditNote', { code: note.code, amount: note.unappliedTotal })}
        </Link>
        {standing ? (
          <span className="text-xs opacity-80">
            ({t('receipts.applyCreditStanding', { invoice: note.invoiceCode })})
          </span>
        ) : null}
        <Gated permission="sales.approve" reasonKey="perm.cannot.postSalesReturns" className="ms-auto">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => void onApply()}
            disabled={applyMutation.isPending || view.over || view.allocations.length === 0}
          >
            {t('receipts.applyCredit')}
          </Button>
        </Gated>
      </div>
      <div className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
        {invoices.map((invoice) => {
          const over = view.overRows.has(invoice.invoiceId);
          return (
            <label
              key={invoice.invoiceId}
              className={cn('flex items-center gap-2', over && 'text-red-700 dark:text-red-300')}
            >
              <span className="w-24 shrink-0 font-mono text-xs">
                <bdi dir="ltr">{invoice.invoiceCode}</bdi>
              </span>
              <Input
                inputMode="decimal"
                dir="ltr"
                aria-label={t('receipts.applyCreditAmount', { code: note.code, invoice: invoice.invoiceCode })}
                aria-invalid={over || undefined}
                className="h-8 bg-background text-end font-mono tabular-nums"
                value={amounts[invoice.invoiceId] ?? ''}
                onChange={(event) =>
                  setAmounts((current) => ({ ...current, [invoice.invoiceId]: event.target.value }))
                }
              />
            </label>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-4 font-mono text-xs">
        <span>
          {t('receipts.totalApplied')} <Amount value={view.total} currency={currency} />
        </span>
        <span className={cn(view.over && 'font-semibold text-red-700 dark:text-red-300')}>
          {view.over ? t('returns.applyOver') : <>{t('returns.unappliedCredit')} <Amount value={view.remaining} currency={currency} /></>}
        </span>
      </div>
      {error ? (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
